import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DocumentAttachment } from '../../../models/routing-slip.model';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { DocumentService } from '../../../services/document.service';

@Component({
  selector: 'app-document-viewer-modal',
  standalone: true,
  imports: [CommonModule, ButtonModule, DialogModule, PdfViewerModule],
  templateUrl: './document-viewer-modal.component.html',
  styleUrl: './document-viewer-modal.component.scss',
})
export class DocumentViewerModalComponent {
  @Input() visible: boolean = false;
  @Input() attachment: DocumentAttachment | null = null;
  @Input() loading: boolean = false;
  @Input() documentName?: string; // Document name from routing slip
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() download = new EventEmitter<DocumentAttachment>();
  @Output() openInNewTab = new EventEmitter<DocumentAttachment>();

  constructor(private documentService: DocumentService) {}

  get pdfSrc(): string | Uint8Array | undefined {
    if (this.attachment && this.isPdfFile()) {
      return this.documentService.getDocumentSource(this.attachment);
    }
    return undefined;
  }

  isPdfFile(): boolean {
    return this.attachment?.type === 'application/pdf';
  }

  getFileTypeDisplay(type?: string): string {
    if (!type) return 'Unknown';

    switch (type) {
      case 'application/pdf':
        return 'PDF Document';
      case 'application/msword':
        return 'Word Document (.doc)';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'Word Document (.docx)';
      case 'text/plain':
        return 'Text File';
      default:
        return type;
    }
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-GB');
  }

  /**
   * Get display name: DocumentName.extension
   * If documentName is provided, use it with the extension from attachment.name
   * Otherwise, fall back to attachment.name
   */
  getDisplayName(): string {
    if (!this.attachment?.name) {
      return '';
    }

    // If documentName is provided, combine it with the file extension
    if (this.documentName) {
      const extension = this.getFileExtension(this.attachment.name);
      return extension ? `${this.documentName}.${extension}` : this.documentName;
    }

    // Fallback to attachment name if no documentName is provided
    return this.attachment.name;
  }

  /**
   * Extract file extension from filename
   */
  private getFileExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
      return '';
    }
    return filename.substring(lastDotIndex + 1).toLowerCase();
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onDownload(): void {
    if (this.attachment) {
      this.download.emit(this.attachment);
    }
  }

  onOpenInNewTab(): void {
    if (this.attachment) {
      this.openInNewTab.emit(this.attachment);
    }
  }

  onPdfLoaded(pdf: any): void {
  }

  onPdfError(error: any): void {
  }
}
